import { FunctionComponent } from "react";

import {
  FieldItem,
  FieldItemStyleProps,
  FieldItemStyles,
  IStyleFunctionOrObject,
  Option,
  OptionsSelect,
  OptionsSelectProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme,
  getFieldErrorMessage,
  useFieldContext,
  useFieldRegister,
  useFormSubscription
} from "@bps/fluent-ui";
import { useLicenceTypes } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { LicenceTypeDto } from "@libs/api/gateways/plt/plt-gateway.dtos";

export type FormLicenceTypesSelectProps = Omit<
  OptionsSelectProps,
  "options" | "selectedKeys" | "onChangeSelectedKeys" | "label"
> & {
  fieldItemStyles?: IStyleFunctionOrObject<
    FieldItemStyleProps,
    FieldItemStyles
  >;
  name: string;
  label?: string;
  validateOnInitialize?: boolean;
};
export const FormLicenceTypesSelect: FunctionComponent<FormLicenceTypesSelectProps> = ({
  name,
  fieldItemStyles,
  validateOnInitialize,
  label,
  ...rest
}) => {
  const theme = useTheme();
  useFieldRegister(name);
  const { value, setFieldValue, meta } = useFieldContext<
    string | string[] | undefined
  >(name);

  const {
    state: { triedToSubmit }
  } = useFormSubscription({ subscriptions: { triedToSubmit: true } });

  const { isLoading, error, data = [] } = useLicenceTypes();

  const sortedData = Array.from(data).sort((a, b) => {
    if (a.isInactive === b.isInactive) return 0;
    return a.isInactive ? 1 : -1;
  });

  const options: Option<LicenceTypeDto>[] = sortedData.map(data => ({
    key: data?.code ?? "",
    text: data.text ?? "",
    disabled: data.isInactive,
    data
  }));

  const renderInactiveOption = (option: Option<LicenceTypeDto>) => (
    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
      <Text
        styles={{
          root: {
            color: theme.semanticColors.disabledText
          }
        }}
      >
        {option.text}
      </Text>
      <TextBadge
        badgeSize={TextBadgeSize.small}
        badgeColor={TextBadgeColor.red}
      >
        Inactive
      </TextBadge>
    </Stack>
  );

  return (
    <FieldItem
      name={name}
      label={label}
      errorMessage={
        meta
          ? getFieldErrorMessage(meta, {
              triedToSubmit,
              validateOnInitialize
            })
          : undefined
      }
      styles={fieldItemStyles}
    >
      <OptionsSelect
        options={options}
        selectedKeys={value}
        onChangeSelectedKeys={setFieldValue}
        errorMessage={error?.message}
        loading={isLoading}
        hideSearchOption
        styles={{ fieldContent: { span: { width: "100%" } } }}
        onRenderOption={option =>
          option.data.isInactive ? (
            renderInactiveOption(option)
          ) : (
            <>{option.text}</>
          )
        }
        {...rest}
      />
    </FieldItem>
  );
};
